import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, Download, CheckCircle, XCircle, Loader, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import CMSAuth from '../components/CMSAuth';
import { cmsFetch } from '../lib/cmsApi';

const COLUMNS = [
  'title', 'description', 'practical_summary', 'distance', 'duration',
  'difficulty', 'region', 'categories', 'neighborhood', 'country', 'city',
  'tags', 'meta_title', 'meta_description', 'seo_tags', 'status',
  'starting_address', 'ending_address',
];

const REQUIRED = ['title'];

const SAMPLE_ROW = {
  title: 'Silver Lake Reservoir Loop',
  description: 'A scenic 2.5-mile loop around Silver Lake Reservoir with views of the Hollywood Sign and Griffith Observatory.',
  practical_summary: 'Flat loop around the reservoir. Street parking on Silver Lake Blvd. Best at golden hour.',
  distance: '2.5',
  duration: '60',
  difficulty: 'Easy',
  region: 'Silver Lake',
  categories: 'scenic-walks,nature-parks',
  neighborhood: 'silver-lake',
  country: 'us',
  city: 'los-angeles',
  tags: 'easy,flat,scenic,family-friendly',
  meta_title: 'Silver Lake Reservoir Loop Walk | Wanderloop',
  meta_description: 'Walk the scenic 2.5-mile loop around Silver Lake Reservoir with views of the Hollywood Sign.',
  seo_tags: 'silver lake walk,silver lake reservoir,things to do silver lake',
  status: 'draft',
  starting_address: '1850 Silver Lake Blvd, Los Angeles, CA 90026',
  ending_address: '1850 Silver Lake Blvd, Los Angeles, CA 90026',
};

const parseCSV = (text) => {
  // Detect delimiter: tab or comma
  const firstLine = text.split('\n')[0];
  const delimiter = firstLine.includes('\t') ? '\t' : ',';

  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  // Parse header
  const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, ''));

  // Parse rows
  return lines.slice(1).filter(line => line.trim()).map(line => {
    // Handle quoted CSV fields
    const values = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') { inQuotes = !inQuotes; continue; }
      if (char === delimiter && !inQuotes) { values.push(current.trim()); current = ''; continue; }
      current += char;
    }
    values.push(current.trim());

    const row = {};
    headers.forEach((h, i) => { row[h] = values[i] || ''; });
    return row;
  });
};

const generateCSVTemplate = () => {
  const header = COLUMNS.join(',');
  const sample = COLUMNS.map(col => {
    const val = SAMPLE_ROW[col] || '';
    return val.includes(',') ? `"${val}"` : val;
  }).join(',');

  const reference = [
    '',
    '# ── REFERENCE: Valid Values ──────────────────────────────',
    '#',
    '# difficulty: Easy | Moderate | Hard',
    '#',
    '# categories (comma-separated): architecture | hidden-gems | scenic-walks | historic-walks | food-focused-walks | nature-parks | family-friendly | sunset-walks',
    '#',
    '# status: draft | published',
    '#',
    '# tags — Difficulty & Pace: easy | moderate | challenging | flat | hilly | steep | beginner-friendly | strenuous',
    '# tags — Duration: under-1-hour | 1-2-hours | half-day | full-day | epic',
    '# tags — Fitness: fitness | high-mileage | leisurely | power-walk | training-route',
    '# tags — Vibe & Mood: adventurous | romantic | solo | meditative | social | photogenic | off-the-beaten-path',
    '# tags — Who Its For: dog-friendly | family-friendly | kid-friendly | senior-friendly | solo-traveler | first-time-visitor',
    '# tags — Terrain: coastal | canyon | urban-grid | waterfront | hillside | tree-lined | rooftop-views | beachfront',
    '# tags — Time of Day: sunrise | sunset | night-walk | golden-hour | midday',
    '# tags — Practical: parking-easy | transit-accessible | shade-heavy | minimal-shade | restrooms-available | free',
    '#',
    '# country: us (default)',
    '# city: los-angeles (default) | chicago | new-york | san-francisco | etc.',
  ].join('\n');

  return `${header}\n${sample}\n${reference}`;
};

const CMSBulkImport = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);

  if (!hasAccess) {
    return <CMSAuth onAuthenticated={() => setHasAccess(true)} />;
  }

  const handleParse = () => {
    const parsed = parseCSV(pasteText);
    setRows(parsed);
    setResults(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      setPasteText(text);
      setRows(parseCSV(text));
      setResults(null);
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wanderloop-walk-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (rows.length === 0) return;
    setImporting(true);
    setProgress({ done: 0, total: rows.length });
    setResults(null);

    try {
      const walkData = rows.map(row => ({
        title: row.title || '',
        description: row.description || '',
        practical_summary: row.practical_summary || '',
        distance: row.distance ? parseFloat(row.distance) : null,
        duration: row.duration ? parseInt(row.duration, 10) : null,
        difficulty: row.difficulty || 'Easy',
        region: row.region || '',
        categories: row.categories || '',
        neighborhood: row.neighborhood || '',
        country: row.country || 'us',
        city: row.city || 'los-angeles',
        tags: row.tags || '',
        meta_title: row.meta_title || '',
        meta_description: row.meta_description || '',
        seo_tags: row.seo_tags || '',
        status: row.status || 'draft',
        starting_address: row.starting_address || '',
        ending_address: row.ending_address || '',
      }));

      const res = await cmsFetch('/api/cms/walks/bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walks: walkData }),
      });

      const data = await res.json();
      setResults(data);
      setProgress({ done: data.created + data.failed, total: data.total });
    } catch (err) {
      setResults({ total: rows.length, created: 0, failed: rows.length, results: [{ row: 0, status: 'error', error: err.message }] });
    } finally {
      setImporting(false);
    }
  };

  const getValidationErrors = (row) => {
    const errors = [];
    REQUIRED.forEach(field => {
      if (!row[field]?.trim()) errors.push(field);
    });
    return errors;
  };

  const totalErrors = rows.reduce((sum, row) => sum + getValidationErrors(row).length, 0);

  return (
    <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link to="/cms/walks">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bulk Import Walks</h1>
              <p className="text-sm text-gray-500 mt-1">
                Import multiple walks from a CSV or spreadsheet
              </p>
            </div>
          </div>

          {/* Step 1: Template + Input */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                1. Prepare your data
              </h2>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="h-4 w-4 mr-1.5" />
                Download CSV Template
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Paste tab-separated (from Google Sheets/Excel) or comma-separated data below.
              The first row must be column headers. Only <strong>title</strong> is required — all other fields are optional.
            </p>

            <div className="space-y-3">
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={8}
                className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                placeholder={`title\tdescription\tdifficulty\tregion\tcategories\n${SAMPLE_ROW.title}\t${SAMPLE_ROW.description}\t${SAMPLE_ROW.difficulty}\t${SAMPLE_ROW.region}\t${SAMPLE_ROW.categories}`}
              />
              <div className="flex items-center gap-3">
                <Button onClick={handleParse} disabled={!pasteText.trim()}>
                  <Upload className="h-4 w-4 mr-1.5" />
                  Parse Data
                </Button>
                <span className="text-gray-400 text-sm">or</span>
                <label className="cursor-pointer">
                  <input type="file" accept=".csv,.tsv,.txt" onChange={handleFileUpload} className="hidden" />
                  <span className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors">
                    <Upload className="h-4 w-4" />
                    Upload CSV File
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Step 2: Preview */}
          {rows.length > 0 && !results && (
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  2. Preview ({rows.length} walk{rows.length !== 1 ? 's' : ''})
                </h2>
                {totalErrors > 0 && (
                  <span className="inline-flex items-center gap-1 text-sm text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    {totalErrors} validation error{totalErrors !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs">#</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs">Title *</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs">Region</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs">Difficulty</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs">Distance</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs">Duration</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs">Categories</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row, i) => {
                      const errors = getValidationErrors(row);
                      return (
                        <tr key={i} className={errors.length ? 'bg-red-50/50' : 'hover:bg-gray-50'}>
                          <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                          <td className={`px-3 py-2 font-medium ${errors.includes('title') ? 'text-red-600' : 'text-gray-900'}`}>
                            {row.title || <span className="text-red-400 italic">Missing</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{row.region || '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{row.difficulty || 'Easy'}</td>
                          <td className="px-3 py-2 text-gray-600">{row.distance ? `${row.distance} mi` : '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{row.duration ? `${row.duration} min` : '—'}</td>
                          <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate">{row.categories || '—'}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              row.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {row.status || 'draft'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3 mt-4">
                <Button
                  onClick={handleImport}
                  disabled={importing || totalErrors > 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {importing ? (
                    <>
                      <Loader className="h-4 w-4 mr-1.5 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-1.5" />
                      Import All ({rows.length} walks)
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => { setRows([]); setPasteText(''); }}>
                  Clear
                </Button>
              </div>

              {/* Progress bar */}
              {importing && progress && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                    <span>Importing walks...</span>
                    <span>{progress.done} / {progress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${(progress.done / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Results */}
          {results && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">3. Import Results</h2>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">{results.total}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{results.created}</p>
                  <p className="text-sm text-green-600">Created</p>
                </div>
                <div className={`rounded-lg p-4 text-center ${results.failed > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <p className={`text-2xl font-bold ${results.failed > 0 ? 'text-red-700' : 'text-gray-400'}`}>{results.failed}</p>
                  <p className={`text-sm ${results.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>Failed</p>
                </div>
              </div>

              {/* Individual results */}
              <div className="space-y-2">
                {results.results?.map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      r.status === 'created' ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {r.status === 'created' ? (
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      )}
                      <span className="text-sm font-medium text-gray-900">
                        Row {r.row}: {r.title || 'Untitled'}
                      </span>
                      {r.status === 'error' && (
                        <span className="text-xs text-red-600 ml-2">— {r.error}</span>
                      )}
                    </div>
                    {r.status === 'created' && r.id && (
                      <Link
                        to={`/cms/walks/${r.id}/edit`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Edit Walk →
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => { setRows([]); setPasteText(''); setResults(null); }}>
                  Import More
                </Button>
                <Link to="/cms/walks">
                  <Button variant="outline">Back to Walk Management</Button>
                </Link>
              </div>
            </div>
          )}

          {/* Column reference */}
          {!rows.length && !results && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Column Reference</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                  {[
                    ['title *', 'Walk title (required)'],
                    ['description', 'Full walk description'],
                    ['practical_summary', 'Brief practical overview'],
                    ['distance', 'Distance in miles (number)'],
                    ['duration', 'Duration in minutes (number)'],
                    ['difficulty', null],
                    ['region', 'Neighborhood/area display name'],
                    ['categories', null],
                    ['neighborhood', 'URL slug for neighborhood'],
                    ['country', 'Country code (default: us)'],
                    ['city', 'City slug (default: los-angeles)'],
                    ['tags', null],
                    ['meta_title', 'SEO page title'],
                    ['meta_description', 'SEO meta description'],
                    ['seo_tags', 'Comma-separated SEO keywords'],
                    ['status', null],
                    ['starting_address', 'Walk start address'],
                    ['ending_address', 'Walk end address'],
                  ].map(([col, desc]) => (
                    <div key={col} className="flex gap-2 py-1">
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono text-purple-700 flex-shrink-0">{col}</code>
                      <span className="text-gray-600">{desc || '(see valid values below)'}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Accepted Values</h2>
                <div className="space-y-4 text-sm">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">difficulty</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {['Easy', 'Moderate', 'Hard'].map(v => (
                        <code key={v} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">{v}</code>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">categories <span className="font-normal text-gray-500">(comma-separated)</span></h3>
                    <div className="flex flex-wrap gap-1.5">
                      {['architecture', 'hidden-gems', 'scenic-walks', 'historic-walks', 'food-focused-walks', 'nature-parks', 'family-friendly', 'sunset-walks'].map(v => (
                        <code key={v} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">{v}</code>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">status</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {['draft', 'published'].map(v => (
                        <code key={v} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">{v}</code>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">tags <span className="font-normal text-gray-500">(comma-separated, by group)</span></h3>
                    <div className="space-y-2">
                      {[
                        ['Difficulty & Pace', 'easy, moderate, challenging, flat, hilly, steep, beginner-friendly, strenuous'],
                        ['Duration', 'under-1-hour, 1-2-hours, half-day, full-day, epic'],
                        ['Fitness & Intensity', 'fitness, high-mileage, leisurely, power-walk, training-route'],
                        ['Vibe & Mood', 'adventurous, romantic, solo, meditative, social, photogenic, off-the-beaten-path'],
                        ['Who It\'s For', 'dog-friendly, family-friendly, kid-friendly, senior-friendly, solo-traveler, first-time-visitor'],
                        ['Terrain & Environment', 'coastal, canyon, urban-grid, waterfront, hillside, tree-lined, rooftop-views, beachfront'],
                        ['Time of Day', 'sunrise, sunset, night-walk, golden-hour, midday'],
                        ['Practical', 'parking-easy, transit-accessible, shade-heavy, minimal-shade, restrooms-available, free'],
                      ].map(([group, values]) => (
                        <div key={group}>
                          <span className="text-xs font-medium text-gray-500">{group}:</span>
                          <span className="text-xs text-gray-600 ml-1">{values}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default CMSBulkImport;
